import React, { useState, useEffect } from 'react';
import { Newspaper, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NewsBanner = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchRecentNews();
  }, []);

  const fetchRecentNews = async () => {
    try {
      const response = await axios.get(`${API}/news/recent?limit=4`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching recent news:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const openArticle = (article) => {
    setSelectedArticle(article);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedArticle(null);
  };

  // Don't render if no news
  if (loading || news.length === 0) {
    return null;
  }

  // Duplicate news items for seamless infinite scroll
  const duplicatedNews = [...news, ...news, ...news, ...news];

  return (
    <>
      {/* News Banner */}
      <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 text-white py-2 overflow-hidden relative">
        <div className="flex items-center">
          {/* Static Label */}
          <div className="flex-shrink-0 bg-emerald-800 px-4 py-1 flex items-center space-x-2 z-10 shadow-lg">
            <Newspaper className="w-4 h-4" />
            <span className="font-semibold text-sm uppercase tracking-wide">Latest News</span>
          </div>
          
          {/* Scrolling News Container */}
          <div className="flex-1 overflow-hidden">
            <div className="animate-scroll flex whitespace-nowrap">
              {duplicatedNews.map((article, index) => (
                <button
                  key={`${article.id}-${index}`}
                  onClick={() => openArticle(article)}
                  className="inline-flex items-center px-6 py-1 hover:bg-white/10 transition-colors duration-200 cursor-pointer group"
                  data-testid={`news-banner-item-${index}`}
                >
                  <span className="text-emerald-200 text-xs mr-2">
                    {formatDate(article.publishedAt)}
                  </span>
                  <span className="text-white text-sm font-medium truncate max-w-xs group-hover:text-emerald-100">
                    {article.title}
                  </span>
                  <ChevronRight className="w-4 h-4 ml-2 text-emerald-300 group-hover:text-white transition-colors" />
                  <span className="mx-4 text-emerald-400">|</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Article Modal */}
      {isModalOpen && selectedArticle && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-emerald-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Newspaper className="w-5 h-5" />
                <span className="font-semibold">News Article</span>
              </div>
              <button 
                onClick={closeModal}
                className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              {/* Article Image */}
              {selectedArticle.image && (
                <img 
                  src={selectedArticle.image.startsWith('/') ? `${BACKEND_URL}${selectedArticle.image}` : selectedArticle.image}
                  alt={selectedArticle.title}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
              )}

              {/* Article Meta */}
              <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs font-medium uppercase">
                  {selectedArticle.category}
                </span>
                <span>{formatDate(selectedArticle.publishedAt)}</span>
                {selectedArticle.author && (
                  <span>By {selectedArticle.author}</span>
                )}
              </div>

              {/* Article Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                {selectedArticle.title}
              </h2>

              {/* Article Summary */}
              <p className="text-gray-600 mb-4 font-medium">
                {selectedArticle.summary}
              </p>

              {/* Article Content */}
              <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                {selectedArticle.content}
              </div>

              {/* Documents */}
              {selectedArticle.documents && selectedArticle.documents.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Attachments</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedArticle.documents.map((doc, idx) => (
                      <a
                        key={idx}
                        href={doc.url.startsWith('/') ? `${BACKEND_URL}${doc.url}` : doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {doc.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-3 flex justify-end">
              <button
                onClick={closeModal}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style jsx="true">{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-scroll {
          animation: scroll 30s linear infinite;
        }
        
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </>
  );
};

export default NewsBanner;
